import { AppContext } from "App";
import { formatDistanceToNow } from "date-fns";
import moment from "moment";
import { useContext, useEffect, useState } from "react";

export const convertToUtc = (val) => {
  return moment(val, "DD/MM/YYYY, HH:mm:ss", true).isValid()
    ? moment(val, "DD/MM/YYYY, HH:mm:ss").toISOString()
    : "";
};

export const formatDate = (date, format = "DD MMMM, YYYY, hh:mm A") => {
  return date ? moment.utc(date).local().format(format) : "-";
};

export const GetPrintLayout = (printType) => {
  switch (printType) {
    case 1:
      return {
        columns: 2,
        rows: 1,
      };
    case 2:
      return {
        columns: 2,
        rows: 1,
      };
    case 3:
      return {
        columns: 2,
        rows: 1,
      };
    case 4:
      return {
        columns: 2,
        rows: 1,
      };
    case 5:
      return {
        columns: 2,
        rows: 1,
      };
    case 6:
      return {
        columns: 2,
        rows: 1,
      };
    case 7:
      return {
        columns: 2,
        rows: 1,
      };
    default:
      return {
        columns: 2,
        rows: 1,
      };
  }
};

export function downloadBlobFromResponse(
  response,
  fallbackFileName = "download.xlsx"
) {
  const blob = new Blob([response.data]);

  // Try to extract filename from Content-Disposition
  let fileName = fallbackFileName;
  const disposition = response.headers["content-disposition"];
  if (disposition && disposition.includes("filename=")) {
    const match = disposition.match(/filename="?(.+?)"?$/);
    if (match?.[1]) {
      fileName = decodeURIComponent(match[1]);
    }
  }

  // Trigger browser download
  const link = document.createElement("a");
  link.href = window.URL.createObjectURL(blob);
  link.setAttribute("download", fileName);
  document.body.appendChild(link);
  link.click();
  link.remove();
}

export const splitName = (fullName) => {
  if (!fullName) return { firstName: "", lastName: "" };

  const parts = fullName.trim().split(/\s+/); // handles multiple spaces
  const firstName = parts[0] || "";
  const lastName = parts.slice(1).join(" ") || "";

  return { firstName, lastName };
};

export const formatAddress = (data) => {
  const { address1, address2, city, state, zip } = data;

  const addressLine = [
    address1,
    address2,
    [city, state, zip].filter(Boolean).join(", "),
  ];
  return addressLine.filter(Boolean).join("\n");
};

export const DiscountAdjustment = (originalAmt, discountAmt, returnAmt) => {
  // Calculate the effective discount percentage
  const discountPercentage = discountAmt / originalAmt;

  // Calculate what portion of the return amount should have discount deducted
  const discountOnReturn = returnAmt * discountPercentage;

  const refundAmt = returnAmt - discountOnReturn;

  return { discountOnReturn, refundAmt };
};

export const WinDims = () => {
  const hasWindow = typeof window !== "undefined";
  function getWindowDimensions() {
    const width = hasWindow ? window.innerWidth : null;
    const height = hasWindow ? window.innerHeight : null;
    return {
      width,
      height,
    };
  }
  const [windowDimensions, setWindowDimensions] = useState(
    getWindowDimensions()
  );
  useEffect(() => {
    if (hasWindow) {
      function handleResize() {
        setWindowDimensions(getWindowDimensions());
      }

      window.addEventListener("resize", handleResize);
      return () => window.removeEventListener("resize", handleResize);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasWindow]);
  return windowDimensions;
};

export const GetHours = (
  startTime = moment().set({ hour: 8, minute: 0, second: 0 }),
  endTime = moment().set({ hour: 22, minute: 0, second: 0 })
) => {
  // Create an array to store the times
  const timeList = [];

  // Start from the startTime and add one hour until we reach or exceed the endTime
  let currentTime = startTime.clone();
  while (currentTime.isSameOrBefore(endTime)) {
    timeList.push(currentTime.format("h:mm A"));
    currentTime.add(1, "hour");
  }
  return timeList;
};

export const GetMonths = () => {
  const date = new Date();
  return [
    `01/${date.getFullYear()}`,
    `02/${date.getFullYear()}`,
    `03/${date.getFullYear()}`,
    `04/${date.getFullYear()}`,
    `05/${date.getFullYear()}`,
    `06/${date.getFullYear()}`,
    `07/${date.getFullYear()}`,
    `08/${date.getFullYear()}`,
    `09/${date.getFullYear()}`,
    `10/${date.getFullYear()}`,
    `11/${date.getFullYear()}`,
    `12/${date.getFullYear()}`,
  ];
};

export const IsEmptyObj = (obj) =>
  obj !== undefined && obj !== null && Object.keys(obj).length === 0
    ? true
    : false;

export const RmvEmptyProp = (obj) =>
  !IsEmptyObj(obj) &&
  Object.fromEntries(
    Object.keys(obj)
      .filter((k) => obj[k])
      .map((k) => [k, obj[k]])
  );

export const RmvArryEmptyProp = (val) =>
  Object.fromEntries(
    Object.keys(val)
      .filter((k) => val[k])
      .map((key) => [
        key,
        Array.isArray(val[key])
          ? val[key].map((j) => RmvEmptyProp(j))
          : val[key],
      ])
  );

export const GetDueDate = (date) => {
  return `${moment(date, "mm/DD/yyyy").format("DD")}/${moment().format(
    "MM/yyyy"
  )}`;
};

export const CleanObject = (obj) => {
  if (
    obj === null ||
    obj === undefined ||
    (typeof obj === "string" && obj.trim() === "")
  ) {
    return undefined;
  }

  if (Array.isArray(obj)) {
    const cleanedArray = obj
      .map((item) => CleanObject(item))
      .filter((item) => item !== undefined);
    return cleanedArray.length ? cleanedArray : undefined;
  }

  if (typeof obj === "object" && !(obj instanceof File)) {
    const cleanedEntries = Object.entries(obj)
      .map(([key, value]) => [key, CleanObject(value)])
      .filter(([, value]) => value !== undefined);

    return cleanedEntries.length
      ? Object.fromEntries(cleanedEntries)
      : undefined;
  }

  return obj;
};

export const ToFormData = (obj, formData = new FormData(), parentKey = "") => {
  const object = CleanObject(obj);

  if (Array.isArray(object)) {
    object.forEach((item, index) => {
      const arrayKey = parentKey ? `${parentKey}[${index}]` : `${index}`;
      if (typeof item === "object" && !(item instanceof File)) {
        ToFormData(item, formData, arrayKey);
      } else {
        formData.append(arrayKey, item);
      }
    });
  } else if (
    object &&
    typeof object === "object" &&
    !(object instanceof File)
  ) {
    Object.keys(object).forEach((key) => {
      const value = object[key];
      const newKey = parentKey ? `${parentKey}[${key}]` : key;

      if (Array.isArray(value)) {
        if (value.every((item) => item instanceof File)) {
          value.forEach((item) => {
            formData.append(newKey, item); // multiple files
          });
        } else {
          value.forEach((item, index) => {
            const arrayKey = `${newKey}[${index}]`;
            ToFormData(item, formData, arrayKey);
          });
        }
      } else if (value instanceof File) {
        formData.append(newKey, value);
      } else if (typeof value === "object") {
        ToFormData(value, formData, newKey);
      } else {
        formData.append(newKey, value);
      }
    });
  } else {
    if (parentKey) formData.append(parentKey, object);
  }

  return formData;
};

// export const ToFormData = (obj, formData = new FormData(), parentKey = "") => {
//   // Clean the object before converting to FormData
//   const object = CleanObject(obj);

//   if (object && typeof object === "object" && !(object instanceof File)) {
//     Object.keys(object).forEach((key) => {
//       const value = object[key];
//       const newKey = parentKey ? `${parentKey}[${key}]` : key;

//       if (Array.isArray(value)) {
//         // Check if the array contains File objects
//         if (value.every((item) => item instanceof File)) {
//           // If all items are files, append them as multiple files
//           value.forEach((item, index) => {
//             formData.append(newKey, item); // Append each file in the array
//           });
//         } else {
//           // Handle arrays of objects (like sellPrices)
//           value.forEach((item, index) => {
//             const arrayKey = `${newKey}[${index}]`;
//             ToFormData(item, formData, arrayKey); // Recursively process each item
//           });
//         }
//       } else if (value instanceof File) {
//         formData.append(newKey, value); // Append single file
//       } else if (typeof value === "object") {
//         ToFormData(value, formData, newKey); // Recursively process nested objects
//       } else {
//         formData.append(newKey, value); // Append non-object values
//       }
//     });
//   } else {
//     formData.append(parentKey, object); // Append non-object values
//   }

//   return formData;
// };

export const NestedFormData = (object, keyName = "imgFiles") => {
  const formData = new FormData();
  Object.keys(object).forEach((key) => {
    if (!Array.isArray(object[key])) formData.append(key, object[key]);
    else if (key === keyName)
      object[key].forEach((val) => formData.append(key, val));
    else
      object[key].forEach((val, i) =>
        Object.keys(val).forEach((key1) =>
          formData.append(`${key}[${i}][${key1}]`, val[key1])
        )
      );
  });
  return formData;
};

export const BGNestedObjToFromData = (obj) => {
  const formData = new FormData();

  const traverse = (current, parentKey = "") => {
    Object.keys(current).forEach((key) => {
      const value = current[key];
      if (value === null || value === undefined || value === "") return;

      const newKey = parentKey ? `${parentKey}.${key}` : key;

      if (value instanceof File) {
        formData.append(newKey, value);
      } else if (Array.isArray(value)) {
        value.forEach((item, index) => {
          if (item instanceof File) {
            formData.append(`${newKey}[${index}]`, item);
          } else if (item instanceof Object) {
            traverse(item, `${newKey}[${index}]`);
          } else {
            formData.append(`${newKey}[${index}]`, item);
          }
        });
      } else if (value instanceof Object) {
        traverse(value, newKey);
      } else {
        formData.append(newKey, value);
      }
    });
  };

  traverse(obj);
  return formData;
};

export const PrintDiv = (printDivId) => {
  var printContents = document.getElementById(printDivId).innerHTML;
  var frame1 = document.createElement("iframe");
  frame1.name = "frame1";
  frame1.style.position = "absolute";
  frame1.style.top = "-1000000px";
  document.body.appendChild(frame1);
  var frameDoc = frame1.contentWindow
    ? frame1.contentWindow
    : frame1.contentDocument.document
    ? frame1.contentDocument.document
    : frame1.contentDocument;

  frameDoc.document.open();
  frameDoc.document.write("<html><head><title></title>");
  frameDoc.document.write("</head><body>");
  frameDoc.document.write(printContents);
  frameDoc.document.write("</body></html>");
  frameDoc.document.close();

  setTimeout(function () {
    window.frames["frame1"].focus();
    window.frames["frame1"].print();
    document.body.removeChild(frame1);
  }, 100);

  return false;
};

export const RandomCode = (length = 10) => {
  if (typeof length !== "number" || length <= 0) {
    throw new Error("Length must be a positive number");
  }

  const maxRandomValue = Math.pow(10, length) - 1; // Calculate the maximum random value
  const randomNum = Math.floor(Math.random() * maxRandomValue); // Generate a random number within the desired length

  return randomNum;
};

export const MaxLength = (val, length = 5) => {
  return val && val.replace(/[^0-9.]/g, "").slice(0, length);
};

export const getDaysByMonth = (month) => {
  const daysInMonth = moment(month, "YYYY/MM").daysInMonth();
  return Array.from({ length: daysInMonth }, (v, k) => k + 1);
};

export const getCurrentWeek = () => {
  const today = moment();
  const startOfWeek = today.clone().startOf("week");
  const endOfWeek = today.clone().endOf("week");

  const weekDays = [];
  let currentDay = startOfWeek.clone();

  while (currentDay.isSameOrBefore(endOfWeek, "day")) {
    weekDays.push(currentDay.format("MMM DD"));
    currentDay.add(1, "day");
  }

  return weekDays;
};

// Locale Storage, Create, Read, Delete, Update
export const lsSave = (key, value) => {
  localStorage.setItem(key, JSON.stringify(value));
};

export const lsGet = (key) => {
  const data = localStorage.getItem(key);
  return data ? JSON.parse(data) : null;
};

export const lsUpdate = (key, newValue) => {
  localStorage.setItem(key, JSON.stringify(newValue));
};

export const lsDelete = (key) => {
  localStorage.removeItem(key);
};

export const getTimeDifference = (date) => {
  const now = new Date();
  const pastDate = new Date(date);

  const difference = Math.abs(now - pastDate) / 1000; // Difference in seconds

  if (difference < 60) return "less than a minute ago";

  return `${formatDistanceToNow(pastDate, { addSuffix: true })}`;
};

export const formatCurrency = (value) => {
  return value.toLocaleString("en-US", { maximumFractionDigits: 2 });
};

export const formatDecimal = (val, localize = false, fixedPlace = 3) => {
  const numericValue = parseFloat(val);
  if (isNaN(numericValue)) return localize ? (0).toFixed(fixedPlace) : 0;

  const fixedVal = parseFloat(numericValue.toFixed(fixedPlace));

  if (localize)
    return parseFloat(fixedVal).toLocaleString("en-US", {
      minimumFractionDigits: fixedPlace,
      maximumFractionDigits: fixedPlace,
    });

  return fixedVal;
};

export const playAudio = (src, volume = 1.0) => {
  try {
    const audio = new Audio(src);
    audio.volume = volume; // Volume ranges from 0.0 to 1.0
    audio.play().catch((error) => {
      console.error("Audio playback failed:", error);
    });
  } catch (err) {
    console.error("Error initializing audio:", err);
  }
};

export const useFormatPrice = () => {
  const { settings } = useContext(AppContext);
  const decimal = settings?.decimal ?? 2;
  const symbol = settings?.currSymbol ?? "$";

  return (price) => symbol + " " + Number(price || 0).toFixed(decimal);
};
